import { useQuery } from "@apollo/client";
import { compact, sortBy } from "lodash";
import { useCallback, useMemo } from "react";
import {
  AssessmentNominationFragment,
  AssessmentType,
  GetComplianceProgramActionsQuery,
  GetComplianceProgramActionsQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import Loading from "@components/loading/loading";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import getComplianceProgramActionsQuery from "../graphql/get-compliance-program-actions-query";
import {
  ComplianceProgramActionBasicItemProps,
  getAssessmentItemsForTemplate,
  getOneononeItemsFromComplianceProgram,
  mapAssessmentDeliveryToItem,
  mapAssessmentForNominationToItem,
} from "../helpers";
import { ProgramActionToDoStateFilter } from "./compliance-program";
import ComplianceProgramActionItem from "./compliance-program-action-item";
import ComplianceProgramActionItemFor1on1 from "./compliance-program-action-item-for-1-on-1";
import ComplianceProgramActionItemForAssessment from "./compliance-program-action-item-for-assessment";
import ComplianceProgramActionItemForNomination from "./compliance-program-action-item-for-nomination";

export function sortByCompletedItemsLast<
  T extends ComplianceProgramActionBasicItemProps
>(items: T[]): T[] {
  return sortBy(items, (item) => {
    return item.statusCompleted ? 1 : 0;
  });
}

const ComplianceProgramMyActions = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const handleChangeActiveReviewFilter = useCallback(
    (filter: ProgramActionToDoStateFilter) => {
      saveUiPreference({ programActionsFilter: filter });
    },
    [saveUiPreference]
  );
  const currentUser = currentUserVar();
  const reportIds = useMemo(
    () =>
      assertEdgesNonNull(currentUser.directReports).map((report) => report.id),
    [currentUser.directReports]
  );
  const label = useLabel();

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<
      GetComplianceProgramActionsQuery,
      GetComplianceProgramActionsQueryVariables
    >(getComplianceProgramActionsQuery, {
      variables: {
        complianceProgramId,
        responderId: currentUser.id,
        deliveryTargetIds: [...reportIds, currentUser.id],
      },
    });

  const complianceProgram = useMemo(
    () => complianceProgramData?.complianceProgram,
    [complianceProgramData]
  );

  const deliveryPackages = complianceProgramData?.assessmentDeliveries
    ? assertEdgesNonNull(complianceProgramData.assessmentDeliveries)
    : [];

  const assessmentOpenForNominations = useMemo(
    () =>
      compact(
        complianceProgramData?.assessmentsOpenForNominations
          ? complianceProgramData.assessmentsOpenForNominations.edges.map(
              (edge) => edge!.node
            )
          : []
      ),
    [complianceProgramData]
  );

  const filterDoneState = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      uiPreferenceCache.programActionsFilter ===
        ProgramActionToDoStateFilter.All ||
      (uiPreferenceCache.programActionsFilter ===
        ProgramActionToDoStateFilter.ToDo &&
        !item.statusCompleted),
    [uiPreferenceCache.programActionsFilter]
  );

  const filterForCurrentUser = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      item.user?.id === currentUser.id && filterDoneState(item),
    [currentUser.id, filterDoneState]
  );
  const filterForDirectReports = useCallback(
    (item: ComplianceProgramActionBasicItemProps) => {
      return (
        item.user?.id &&
        reportIds.includes(item.user.id) &&
        filterDoneState(item)
      );
    },
    [reportIds, filterDoneState]
  );
  const filterForOtherUsers = useCallback(
    (item: ComplianceProgramActionBasicItemProps) => {
      return (
        item.user?.id &&
        item.user.id !== currentUser.id &&
        !reportIds.includes(item.user.id) &&
        filterDoneState(item)
      );
    },
    [currentUser.id, filterDoneState, reportIds]
  );

  const reviewTypeLabel = useMemo(() => {
    if (!!complianceProgram?.engagementSurveyTemplate) {
      return "Survey";
    }
    return label("review", { capitalize: true, pluralize: true });
  }, [complianceProgram?.engagementSurveyTemplate, label]);

  if (isLoadingComplianceProgram) {
    return (
      <div className="flex-1 flex justify-center p-10">
        <Loading>Loading program..</Loading>
      </div>
    );
  }

  if (!complianceProgram) {
    return (
      <div className="flex-1 flex justify-center p-10">Program not found</div>
    );
  }

  const engagementSurveyItems = complianceProgram?.engagementSurveyTemplate
    ? getAssessmentItemsForTemplate(
        complianceProgram,
        complianceProgram.engagementSurveyTemplate,
        currentUser.id
      )
    : [];

  const performanceAssessmentItems =
    complianceProgram?.performanceAssessmentTemplate
      ? getAssessmentItemsForTemplate(
          complianceProgram,
          complianceProgram.performanceAssessmentTemplate,
          currentUser.id
        )
      : [];
  const otherPerformanceAssessmentItems =
    performanceAssessmentItems.filter(filterForOtherUsers);
  const myPerformanceAssessmentItems =
    performanceAssessmentItems.filter(filterForCurrentUser);
  const directReportsPerformanceAssessmentItems =
    performanceAssessmentItems.filter(filterForDirectReports);

  const managerAssessmentItems = complianceProgram?.managerAssessmentTemplate
    ? getAssessmentItemsForTemplate(
        complianceProgram,
        complianceProgram.managerAssessmentTemplate,
        currentUser.id
      )
    : [];
  const otherManagerAssessmentItems =
    managerAssessmentItems.filter(filterForOtherUsers);
  const myManagerAssessmentItems =
    managerAssessmentItems.filter(filterForCurrentUser);
  const directReportsManagerAssessmentItems = managerAssessmentItems.filter(
    filterForDirectReports
  );

  const peerAssessmentItems = complianceProgram?.peerAssessmentTemplate
    ? getAssessmentItemsForTemplate(
        complianceProgram,
        complianceProgram.peerAssessmentTemplate,
        currentUser.id
      )
    : [];
  const myPeerAssessmentItems =
    peerAssessmentItems.filter(filterForCurrentUser);
  const otherPeerAssessmentItems =
    peerAssessmentItems.filter(filterForOtherUsers);
  const directReportsPeerAssessmentItems = peerAssessmentItems.filter(
    filterForDirectReports
  );

  const deliveryPackageItems: ComplianceProgramActionBasicItemProps[] =
    deliveryPackages.map((delivery) =>
      mapAssessmentDeliveryToItem(currentUser.id, delivery)
    );
  const myDeliveryItems = deliveryPackageItems.filter(filterForCurrentUser);
  const otherDeliveryItems = deliveryPackageItems.filter(filterForOtherUsers);
  const directReportDeliveryItems = deliveryPackageItems.filter(
    filterForDirectReports
  );

  const nominations: (ComplianceProgramActionBasicItemProps & {
    assessmentOpenForNomination: AssessmentNominationFragment;
  })[] = assessmentOpenForNominations.map((assessmentOpenForNomination) =>
    mapAssessmentForNominationToItem(
      currentUser.id,
      assessmentOpenForNomination
    )
  );
  const otherNominations = nominations.filter(filterForOtherUsers);
  const myNominations = nominations.filter(filterForCurrentUser);
  const directReportsNominations = nominations.filter(filterForDirectReports);

  const oneononeItems = getOneononeItemsFromComplianceProgram(
    complianceProgram,
    currentUser.id
  );
  const otherOneononeItems = oneononeItems.filter(filterForOtherUsers);
  const myOneononeItems = oneononeItems.filter(filterForCurrentUser);
  const directReportsOneononeItems = oneononeItems.filter(
    filterForDirectReports
  );

  const myItemsCount =
    engagementSurveyItems.length +
    myPerformanceAssessmentItems.length +
    myManagerAssessmentItems.length +
    myPeerAssessmentItems.length +
    myOneononeItems.length +
    myDeliveryItems.length +
    myNominations.length;
  const directReportsItemsCount =
    directReportsPerformanceAssessmentItems.length +
    directReportsManagerAssessmentItems.length +
    directReportsPeerAssessmentItems.length +
    directReportsOneononeItems.length +
    directReportDeliveryItems.length +
    directReportsNominations.length;
  const otherItemsCount =
    otherPerformanceAssessmentItems.length +
    otherManagerAssessmentItems.length +
    otherPeerAssessmentItems.length +
    otherOneononeItems.length +
    otherDeliveryItems.length +
    otherNominations.length;

  return (
    <div
      className="flex flex-col max-w-screen-lg w-full mx-auto"
      aria-label="Compliance Program my actions"
    >
      <div className="flex pb-8">
        <ToggleButtonGroup<ProgramActionToDoStateFilter>
          theme={ToggleButtonGroupTheme.mergedLightBlueButtons}
          buttons={[
            {
              title: ProgramActionToDoStateFilter.All,
              value: ProgramActionToDoStateFilter.All,
              active:
                uiPreferenceCache.programActionsFilter ===
                ProgramActionToDoStateFilter.All,
            },
            {
              title: ProgramActionToDoStateFilter.ToDo,
              value: ProgramActionToDoStateFilter.ToDo,
              active:
                uiPreferenceCache.programActionsFilter ===
                ProgramActionToDoStateFilter.ToDo,
            },
          ]}
          onClick={(button) =>
            handleChangeActiveReviewFilter(
              button?.value || ProgramActionToDoStateFilter.All
            )
          }
        />
      </div>

      <Layout.MainSection title={`My ${reviewTypeLabel}`}>
        {myItemsCount === 0 ? (
          <Layout.MainSubSectionEmpty>No actions.</Layout.MainSubSectionEmpty>
        ) : (
          <Layout.MainSubSectionList>
            {sortByCompletedItemsLast(engagementSurveyItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              />
            ))}
            {sortByCompletedItemsLast(myPerformanceAssessmentItems).map(
              (item) => (
                <ComplianceProgramActionItemForAssessment
                  key={item.key}
                  item={item}
                />
              )
            )}
            {sortByCompletedItemsLast(myManagerAssessmentItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              />
            ))}
            {sortByCompletedItemsLast(myPeerAssessmentItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              />
            ))}
            {sortByCompletedItemsLast(myOneononeItems).map((item) => (
              <ComplianceProgramActionItemFor1on1 key={item.id} item={item} />
            ))}
            {sortByCompletedItemsLast(myNominations).map((item) => (
              <ComplianceProgramActionItemForNomination
                key={item.key}
                item={item}
              />
            ))}
            {sortByCompletedItemsLast(myDeliveryItems).map(
              ({ key, ...item }) => (
                <ComplianceProgramActionItem key={key} {...item} />
              )
            )}
          </Layout.MainSubSectionList>
        )}
      </Layout.MainSection>

      {reportIds.length > 0 && !complianceProgram?.engagementSurveyTemplate && (
        <Layout.MainSection title="Actions for Direct Reports">
          {directReportsItemsCount === 0 && (
            <Layout.MainSubSectionEmpty>No actions.</Layout.MainSubSectionEmpty>
          )}
          {directReportsPerformanceAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              className="max-w-screen-lg"
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })} for Direct Reports`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  directReportsPerformanceAssessmentItems
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.key}
                    item={item}
                  />
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsManagerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                }
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  directReportsManagerAssessmentItems
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.id}
                    item={item}
                  />
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {directReportsPeerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                }
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsPeerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.id}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {directReportsOneononeItems.length > 0 && (
            <Layout.MainSubSection
              title={`${label("1-on-1", { pluralize: true })} Meetings`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsOneononeItems).map(
                  (item) => (
                    <ComplianceProgramActionItemFor1on1
                      key={item.key}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {directReportsNominations.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { capitalize: true }
              )} Nominations`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsNominations).map(
                  (item) => (
                    <ComplianceProgramActionItemForNomination
                      key={item.key}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {directReportDeliveryItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })} to Deliver`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportDeliveryItems).map(
                  ({ key, ...item }) => (
                    <ComplianceProgramActionItem key={key} {...item} />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
        </Layout.MainSection>
      )}

      {!complianceProgram?.engagementSurveyTemplate && (
        <Layout.MainSection title="Actions for Others">
          {otherItemsCount === 0 && (
            <Layout.MainSubSectionEmpty>No actions.</Layout.MainSubSectionEmpty>
          )}

          {otherPerformanceAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              className="max-w-screen-lg"
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })} for Others`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherPerformanceAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.key}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {otherManagerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                }
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherManagerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.id}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {otherPeerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                }
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherPeerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.id}
                      item={item}
                    />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {otherNominations.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { capitalize: true }
              )} Nominations`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherNominations).map((item) => (
                  <ComplianceProgramActionItemForNomination
                    key={item.key}
                    item={item}
                  />
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {otherDeliveryItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })} to Deliver`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherDeliveryItems).map(
                  ({ key, ...item }) => (
                    <ComplianceProgramActionItem key={key} {...item} />
                  )
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {otherOneononeItems.length > 0 && (
            <Layout.MainSubSection
              title={`${label("1-on-1", { pluralize: true })} Meetings`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherOneononeItems).map((item) => (
                  <ComplianceProgramActionItemFor1on1
                    key={item.key}
                    item={item}
                  />
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
        </Layout.MainSection>
      )}
    </div>
  );
};

export default ComplianceProgramMyActions;
